import React from 'react';
import AddReportDrillDownButton from '../AddFilterPlateButton';
import Row from '../Common/Row';
import FilterPlate from './FilterPlate';

const FilterPlates = ({
  drillDowns,
  scope,
  isScope,
  isReport,
  isDashboardCard,
  onManualFilterChanged,
  onOpened,
  onFilterAdded,
  onFilterRemoved,
  testIdPrefix,
  label,
  isDisabled,
  isDateScopeDisabled,
  hideInheritedFilters,
  isEveryFilterInteractive,
  hideAddFilterButton,
  isAddFilterIconOnly,
  hiddenDrillDownIds,
  leadingChildren,
  children,
}: {
  drillDowns: FilterPlateType[];
  scope: FilterPlateType[];
  isScope?: boolean;
  isReport?: boolean;
  isDashboardCard?: boolean;
  onManualFilterChanged?: () => void;
  onOpened?: () => void;
  onFilterAdded?: () => void;
  onFilterRemoved?: () => void;
  testIdPrefix?: string;
  label?: string;
  isDisabled?: boolean;
  isDateScopeDisabled?: boolean;
  // When true, filter plates that render as disabled (non-interactive) badges
  // are hidden from view. Used by popup reports to suppress the context
  // filters inherited from the parent dashboard gadget (the user's active
  // drill-down stays interactive and visible). The filters remain applied —
  // only their badges are hidden.
  hideInheritedFilters?: boolean;
  // Reports normally only let the last drill-down be changed. A popup
  // report's are its run-time filters, each of which can be removed.
  isEveryFilterInteractive?: boolean;
  // When true the "Add Filter" button is omitted. Used when a screen renders
  // TWO plate rows (scope and drill-downs) and only wants one add affordance.
  hideAddFilterButton?: boolean;
  // The "Add Filter" button as its icon alone, named on hover (e.g. in a
  // crowded top bar)
  isAddFilterIconOnly?: boolean;
  // Drill-downs rendered elsewhere in the row (e.g. in a report's pinned
  // filter slots) and so skipped here
  hiddenDrillDownIds?: string[];
  // Rendered at the start of the row, before the drill-down plates
  leadingChildren?: React.ReactNode;
  children?: React.ReactNode;
}) => (
  <Row style={{ flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
    {leadingChildren}
    {isScope && (
      <React.Fragment>
        {scope.map((d) => {
          return (
            <FilterPlate
              plate={d}
              key={d.id}
              isScope={isScope}
              isReport={isReport}
              isDashboardCard={isDashboardCard}
              onManualFilterChanged={onManualFilterChanged}
              onFilterRemoved={onFilterRemoved}
              isDisabled={isDisabled}
              isDateScopeDisabled={isDateScopeDisabled}
              hideIfDisabled={hideInheritedFilters}
            />
          );
        })}
      </React.Fragment>
    )}
    {!isScope && (
      <React.Fragment>
        {drillDowns
          .filter((d) => !hiddenDrillDownIds?.includes(d.id))
          .map((d) => {
            return (
              <FilterPlate
                plate={d}
                key={d.id}
                isScope={false}
                isReport={isReport}
                isDashboardCard={isDashboardCard}
                onManualFilterChanged={onManualFilterChanged}
                onFilterRemoved={onFilterRemoved}
                isDisabled={isDisabled}
                isDateScopeDisabled={isDateScopeDisabled}
                hideIfDisabled={hideInheritedFilters}
                isAlwaysInteractive={isEveryFilterInteractive}
              />
            );
          })}
      </React.Fragment>
    )}

    {!hideAddFilterButton && (
      <AddReportDrillDownButton
        isScope={!!isScope}
        isReport={isReport}
        onManualFilterChanged={onManualFilterChanged}
        onOpened={onOpened}
        onFilterAdded={onFilterAdded}
        testIdPrefix={testIdPrefix}
        label={label}
        isIconOnly={isAddFilterIconOnly}
        isDisabled={isDisabled}
        isDateScopeDisabled={isDateScopeDisabled}
      />
    )}
    {children}
  </Row>
);

export default FilterPlates;
